<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { CARD_CLASS, FIELD_CLASS, SELECT_CLASS } from '../constants';
import { parseLanguages } from '../residentsForm';

// The tenant languages: one account-wide list in the settings resource. The
// Language choice on a tenant and the languages the bot mirrors come from it.
export default {
  name: 'LanguagesCard',
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      text: '',
      defaultLanguage: '',
      error: '',
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    languages() {
      return parseLanguages(this.text);
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(settings) {
        if (settings) {
          this.text = (settings.languages || []).join(', ');
          this.defaultLanguage = settings.default_language || '';
        }
      },
    },
  },
  methods: {
    ...mapActions('coLivingConfig', ['saveSettings']),
    async onSave() {
      this.error = '';
      if (!this.languages.length) {
        this.error = this.$t('CO_LIVING.RESIDENTS.LANGUAGES.ERRORS.EMPTY');
        return;
      }
      if (!this.languages.includes(this.defaultLanguage)) {
        this.error = this.$t('CO_LIVING.RESIDENTS.LANGUAGES.ERRORS.DEFAULT');
        return;
      }
      this.isSaving = true;
      try {
        await this.saveSettings({
          languages: this.languages,
          default_language: this.defaultLanguage,
        });
        useAlert(this.$t('CO_LIVING.COMMON.SAVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.SETTINGS.SAVE_FAILED'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <section :class="cardClass" data-testid="co-living-languages">
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
      {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.TITLE') }}
    </h2>
    <p class="mt-1 mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.DESCRIPTION') }}
    </p>
    <form class="grid grid-cols-1 gap-4" @submit.prevent="onSave">
      <div>
        <label
          for="co-living-languages-input"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.LIST') }}
        </label>
        <input
          id="co-living-languages-input"
          v-model="text"
          type="text"
          :class="fieldClass"
          data-testid="co-living-languages-input"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.LIST_HINT') }}
        </p>
      </div>
      <div>
        <label
          for="co-living-default-language"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.DEFAULT') }}
        </label>
        <select
          id="co-living-default-language"
          v-model="defaultLanguage"
          :class="selectClass"
          data-testid="co-living-default-language"
        >
          <option
            v-for="language in languages"
            :key="language"
            :value="language"
          >
            {{ language }}
          </option>
        </select>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.RESIDENTS.LANGUAGES.DEFAULT_HINT') }}
        </p>
      </div>
      <p
        v-if="error"
        class="text-xs text-red-500"
        data-testid="co-living-languages-error"
      >
        {{ error }}
      </p>
      <div>
        <woot-button
          type="submit"
          :is-loading="isSaving"
          data-testid="co-living-languages-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </section>
</template>
